import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, Validators, FormGroup, FormBuilder, ValidationErrors } from '@angular/forms';
import { UserPutObject } from '@app/modules/user/user';
import { UserService } from '@app/modules/user/user.service';
import { User, UserRolesMap } from '@app/modules/user/user';
import { UserAvatarService } from '../user-avatar/user-avatar.service';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { AuthenticationService } from '@app/core';
import { formatDate } from '@angular/common';

@Component({
  providers: [ToastrService, UserService, UserAvatarService],
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  standalone: false
})
export class UserProfileComponent implements OnInit {
  error: string | undefined;
  user: User;
  currentUser: User;
  userProfileForm: FormGroup;
  isLoading = false;
  isSaving = false;
  isAdminEditMode = false;

  numericRegEx = RegExp(/^[0-9]{1,7}$/);
  phoneRegEx = RegExp(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/);
  stringRegEx = RegExp(/^[a-z'?]*$/i);
  stringMinimumOneWordRegEx = RegExp(/^(?!\s*$).+/);

  constructor(
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private toastrService: ToastrService,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.currentUser = this.authenticationService.currentUserSubject.getValue();
    const routeUserId = this.route.snapshot.paramMap.get('userId');
    this.isAdminEditMode = !!routeUserId;

    if (routeUserId) {
      if (!this.canManageOtherUsers()) {
        this.router.navigate(['/user/profile']);
        return;
      }

      if (routeUserId === this.currentUser?.userId) {
        this.user = this.currentUser;
        this.createForm();
        return;
      }

      this.loadUserById(routeUserId);
      return;
    }

    this.user = this.route.snapshot.data.user || this.currentUser;
    this.createForm();
  }

  get pageTitle(): string {
    return this.isAdminEditMode ? 'Edit User' : 'User Profile';
  }

  get pageCopy(): string {
    return this.isAdminEditMode
      ? 'Manager and admin edits update the selected user record and avatar without leaving the standard roster flow.'
      : 'Update your profile information and avatar.';
  }

  get canLoginAsUser(): boolean {
    return this.isAdminEditMode && this.getUserRoleValue(this.currentUser) === 1 && !!this.user?.userId && this.user.userId !== this.currentUser?.userId;
  }

  private buildDefaultUserInterests() {
    return {
      celebrity: false,
      helicopter: false,
      kidney: false,
      skydivedOrBungeed: false,
      appearedOnTv: false,
      janeAusten: false,
      escargo: false,
      deployed: false,
      instrument: false,
      seenTornado: false,
      hitchhiked: false,
      DND: false
    };
  }

  private normalizeUserInterests(userInterests: any) {
    const defaultUserInterests = this.buildDefaultUserInterests();

    if (!userInterests) {
      return defaultUserInterests;
    }

    if (typeof userInterests === 'string') {
      try {
        const parsedUserInterests = JSON.parse(userInterests);
        return {
          ...defaultUserInterests,
          ...(parsedUserInterests || {})
        };
      } catch {
        return defaultUserInterests;
      }
    }

    if (typeof userInterests === 'object') {
      return {
        ...defaultUserInterests,
        ...userInterests
      };
    }

    return defaultUserInterests;
  }

  private normalizeUserDob(userDob: any): string {
    if (!userDob) {
      return '';
    }

    if (typeof userDob === 'string') {
      const trimmed = userDob.trim();
      const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
      if (isoMatch) {
        return `${isoMatch[2]}/${isoMatch[3]}/${isoMatch[1]}`;
      }
      const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
      if (slashMatch) {
        return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(2, '0')}/${slashMatch[3]}`;
      }
    }

    try {
      return formatDate(userDob, 'MM/dd/yyyy', 'en');
    } catch {
      return '';
    }
  }

  private normalizeUserDobForApi(userDob: string): string {
    if (!userDob) {
      return '';
    }

    const trimmed = String(userDob).trim();

    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const normalizedDate = `${slashMatch[3]}-${slashMatch[1].padStart(2, '0')}-${slashMatch[2].padStart(2, '0')}`;
      return this.isValidUserDob(normalizedDate) ? normalizedDate : '';
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const normalizedDate = `${isoMatch[1]}-${isoMatch[2].padStart(2, '0')}-${isoMatch[3].padStart(2, '0')}`;
      return this.isValidUserDob(normalizedDate) ? normalizedDate : '';
    }

    if (/^\d{8}$/.test(trimmed)) {
      const first4 = parseInt(trimmed.substring(0, 4), 10);
      let parsedYear: number, parsedMonth: number, parsedDay: number;
      if (first4 >= 1900 && first4 <= 2100) {
        parsedYear = first4;
        parsedMonth = parseInt(trimmed.substring(4, 6), 10);
        parsedDay = parseInt(trimmed.substring(6, 8), 10);
      } else {
        parsedMonth = parseInt(trimmed.substring(0, 2), 10);
        parsedDay = parseInt(trimmed.substring(2, 4), 10);
        parsedYear = parseInt(trimmed.substring(4, 8), 10);
      }
      const normalizedDate = `${parsedYear.toString().padStart(4, '0')}-${parsedMonth.toString().padStart(2, '0')}-${parsedDay.toString().padStart(2, '0')}`;
      return this.isValidUserDob(normalizedDate) ? normalizedDate : '';
    }

    return '';
  }

  private isValidUserDob(normalizedDate: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate || '')) {
      return false;
    }

    const [year, month, day] = normalizedDate.split('-').map(value => parseInt(value, 10));
    const parsedDate = new Date(Date.UTC(year, month - 1, day));
    return (
      parsedDate.getUTCFullYear() === year &&
      parsedDate.getUTCMonth() === month - 1 &&
      parsedDate.getUTCDate() === day
    );
  }

  private readonly userDobValidator = (control: AbstractControl): ValidationErrors | null => {
    const normalizedDate = this.normalizeUserDobForApi(control.value);
    if (!normalizedDate) {
      return { invalidDate: true };
    }

    return normalizedDate > formatDate(new Date(), 'yyyy-MM-dd', 'en') ? { futureDate: true } : null;
  };

  private createForm() {
    if (!this.user) {
      return;
    }

    this.user.userInterests = this.normalizeUserInterests(this.user.userInterests);

    this.userProfileForm = this.fb.group({
      userFirstName: this.fb.control(this.user.userFirstName, [
        Validators.required,
        Validators.pattern(this.stringMinimumOneWordRegEx)
      ]),
      userLastName: this.fb.control(this.user.userLastName, [
        Validators.required,
        Validators.pattern(this.stringMinimumOneWordRegEx)
      ]),
      userEmail: [
        {
          value: this.user.userEmail,
          disabled: true
        }
      ],
      userPhoneCountryCode: [this.user.userCountryCode || '1', [Validators.pattern(this.numericRegEx)]],
      userPhoneAreaCode: [this.user.userAreaCode || '', [Validators.pattern(this.numericRegEx)]],
      userPhoneNumber: [
        this.formatPhoneInputValue(String(this.user.userPhoneNumber || '')),
        [Validators.pattern(this.phoneRegEx)]
      ],
      userDob: this.fb.control(this.normalizeUserDob(this.user.userDob), [Validators.required, this.userDobValidator]),
      userFavoriteDessert: this.fb.control(this.user.userFavoriteDessert),
      userSpeaksSpanish: this.fb.control(this.user.userSpeaksSpanish == true ? '1' : '0'),
      userInterests: this.fb.group({
        celebrity: this.fb.control(this.user.userInterests.celebrity),
        helicopter: this.fb.control(this.user.userInterests.helicopter),
        kidney: this.fb.control(this.user.userInterests.kidney),
        skydivedOrBungeed: this.fb.control(this.user.userInterests.skydivedOrBungeed),
        appearedOnTv: this.fb.control(this.user.userInterests.appearedOnTv),
        janeAusten: this.fb.control(this.user.userInterests.janeAusten),
        escargo: this.fb.control(this.user.userInterests.escargo),
        deployed: this.fb.control(this.user.userInterests.deployed),
        instrument: this.fb.control(this.user.userInterests.instrument),
        seenTornado: this.fb.control(this.user.userInterests.seenTornado),
        hitchhiked: this.fb.control(this.user.userInterests.hitchhiked),
        DND: this.fb.control(this.user.userInterests.DND)
      }),
      userAdditionalInfo: this.fb.control(this.user.userAdditionalInfo)
    });
    this.userProfileForm.updateValueAndValidity();
  }

  private loadUserById(userId: string) {
    this.isLoading = true;
    this.error = undefined;

    this.userService
      .getUserByUserId(userId)
      .pipe(take(1))
      .subscribe({
        next: (user: User) => {
          this.user = user;
          this.createForm();
          this.isLoading = false;
        },
        error: () => {
          this.error = 'Unable to load this user record.';
          this.isLoading = false;
        }
      });
  }

  cancelUpdateProfile() {
    this.router.navigate(this.isAdminEditMode ? ['/users'] : ['/user/profile']);
  }

  loginAsUser() {
    if (!this.canLoginAsUser) {
      return;
    }

    const adminUser = this.currentUser;
    this.userService
      .impersonateUser(adminUser.userId, this.user.userId)
      .pipe(take(1))
      .subscribe((user: User) => {
        if (!user) {
          return;
        }

        this.authenticationService.startImpersonation(user, adminUser).then(() => {
          this.router.navigate(['/home']);
        });
      });
  }

  updateUserProfile() {
    if (this.isSaving || !this.validateControls()) {
      return;
    }
    this.isSaving = true;
    let formSubmission = this.userProfileForm.getRawValue();
    let userPutPayload = this.userFormSubmissionFactory(formSubmission);
    this.userService.updateUserByUserId(this.user.userId, userPutPayload).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastrService
          .success(this.isAdminEditMode ? 'Successfully updated user record!' : 'Successfully updated user profile!')
          .onShown.pipe(take(1))
          .subscribe(() => {
            this.applyFormSubmissionToUser(formSubmission);

            if (this.user?.userId === this.currentUser?.userId) {
              this.authenticationService.currentUserSubject.next(this.user);
              localStorage.setItem('followup-user', JSON.stringify(this.user));
            }

            this.createForm();
            this.router.navigate(this.isAdminEditMode ? ['/users'] : ['/user/profile']);
          });
      },
      error: () => {
        this.isSaving = false;
        this.toastrService.error('Profile was not saved. Your entries are still here; please review them and try again.');
      }
    });
  }

  private applyFormSubmissionToUser(formSubmission: any) {
    this.user.userFirstName = formSubmission.userFirstName;
    this.user.userLastName = formSubmission.userLastName;
    this.user.userCountryCode = formSubmission.userPhoneCountryCode;
    this.user.userAreaCode = formSubmission.userPhoneAreaCode;
    this.user.userPhoneNumber =
      parseInt(this.formatPhoneInputValue(formSubmission.userPhoneNumber).replace(/\D/g, ''), 10) || undefined;
    this.user.userDob = this.normalizeUserDobForApi(formSubmission.userDob) as any;
    this.user.userSpeaksSpanish = formSubmission.userSpeaksSpanish;
    this.user.userFavoriteDessert = formSubmission.userFavoriteDessert;
    this.user.userInterests = JSON.stringify(formSubmission.userInterests);
    this.user.userAdditionalInfo = formSubmission.userAdditionalInfo;
  }

  userFormSubmissionFactory(formSubmission: any): UserPutObject {
    var payload = {};
    let userInterests = JSON.stringify(formSubmission.userInterests);
    payload = {
      userFirstName: formSubmission.userFirstName,
      userLastName: formSubmission.userLastName,
      userCountryCode: formSubmission.userPhoneCountryCode || '1',
      userAreaCode: formSubmission.userPhoneAreaCode || '',
      userPhoneNumber: this.formatPhoneInputValue(formSubmission.userPhoneNumber),
      userSpeaksSpanish: parseInt(formSubmission.userSpeaksSpanish),
      userDob: this.normalizeUserDobForApi(formSubmission.userDob),
      userFavoriteDessert: formSubmission.userFavoriteDessert || '',
      userInterests: userInterests,
      userAdditionalInfo: formSubmission.userAdditionalInfo
    };
    return <UserPutObject>payload;
  }

  private formatPhoneInputValue(phoneValue: string): string {
    if (!phoneValue) {
      return '';
    }

    const digits = phoneValue.toString().replace(/[^0-9]/g, '');

    if (digits.length === 7) {
      return digits.substr(0, 3) + '-' + digits.substr(3);
    }

    if (digits.length === 10) {
      return digits.substr(0, 3) + '-' + digits.substr(3, 3) + '-' + digits.substr(6);
    }

    return phoneValue.toString();
  }

  /**
   * A function to validate form controls
   * and if there are any validation errors,
   * bounce the user to that error while spitting
   * out what the error is in the console.
   */
  validateControls(): boolean {
    this.userProfileForm?.markAllAsTouched();
    this.userProfileForm?.updateValueAndValidity();

    if (!this.userProfileForm || this.userProfileForm.valid) {
      return true;
    }

    const firstError = this.getFirstInvalidControlElement(this.userProfileForm);
    if (firstError) {
      const scrollTarget = (firstError.closest('ion-item, .form-row') as HTMLElement | null) || firstError;
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.focusValidationElement(firstError);
      alert('Unable to save. Please check ' + this.getValidationFieldLabel(firstError) + '.');
      return false;
    }

    alert('Unable to save. Please check the highlighted required fields. Your entries have been preserved.');
    return false;
  }

  private getFirstInvalidControlElement(control: AbstractControl): HTMLElement | null {
    const childControls = (control as FormGroup).controls;
    if (!childControls) {
      return null;
    }

    for (const controlName of Object.keys(childControls)) {
      const childControl = childControls[controlName];
      const nestedElement = this.getFirstInvalidControlElement(childControl);
      if (nestedElement) {
        return nestedElement;
      }
      if (childControl.invalid) {
        return document.querySelector('[formControlName="' + controlName + '"]') as HTMLElement | null;
      }
    }

    return null;
  }

  private getValidationFieldLabel(element: HTMLElement): string {
    const label = element.closest('ion-item, .form-row')?.querySelector('ion-label, label');
    const text = (label?.textContent || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim().replace(/:$/, '');
    return text || 'the first highlighted field';
  }

  private focusValidationElement(element: HTMLElement) {
    const focusTarget = element as HTMLElement & { setFocus?: () => Promise<void> };
    if (focusTarget.setFocus) {
      void focusTarget.setFocus();
    } else {
      focusTarget.focus();
    }
  }

  private getUserRoleValue(user: User): number {
    if (!user) {
      return 0;
    }

    if (typeof user.userLevel === 'number') {
      return user.userLevel;
    }

    return (UserRolesMap as any)[String(user.userLevel)] || 0;
  }

  private canManageOtherUsers(): boolean {
    const userRoleValue = this.getUserRoleValue(this.currentUser);
    return userRoleValue === 1 || userRoleValue === 2;
  }
}
